import { doesRectangleContainPoint } from '../../utils/Geometry';
// eslint-disable-next-line import/no-cycle
import { HandleType } from './SliderBaseWidget';

export interface DragHandle {
  handleType: HandleType;
  directlyOnIcon: boolean;
}

/**
 * This contains logic related to determining which handle to drag given the client coordinates from a mouse or touch
 * event.  The SliderHandleContainer needs this and is the only user.
 *
 * The logic for this at a high level is (even though this is broken into multiple methods):
 * ```
 * if mouse then
 *   if there is only one handle then
 *     return the left handle (clicking anywhere on the track grabs the handle)
 *   otherwise (if there are two handles) then
 *     if the click is between the *ICONS* return saying that both handles should be dragged
 *     otherwise return the handle that is closer to the click
 * otherwise (if touch) then
 *   if there is only one handle then
 *     if the touch is directly on the *INTERACTIVE AREA* for the left handle, return that
 *   otherwise (if there are two handles) then
 *     if the touch is on both the *INTERACTIVE AREA*s for both handles, return the one that is closer to the touch
 *     otherwise if it is on only one of the *INTERACTIVE AREA*s return that
 * and if none of those situations above are true, return nothing
 * ```
 */
export function determineWhichHandleShouldBeDraggedForMouseAndOneHandle(
  clientCoordinates: { x: number, y: number },
  leftHandleIconBoundingClientRect: ClientRect
): DragHandle {

  const point = { left: clientCoordinates.x, top: clientCoordinates.y };
  const directlyOnIcon = doesRectangleContainPoint(leftHandleIconBoundingClientRect, point);
  return { handleType: HandleType.Left, directlyOnIcon };
}

export function determineWhichHandleShouldBeDraggedForMouseAndTwoHandles(
  clientCoordinates: { x: number, y: number },
  leftHandleIconBoundingClientRect: ClientRect,
  rightHandleIconBoundingClientRect: ClientRect
): DragHandle {

  const point = { left: clientCoordinates.x, top: clientCoordinates.y };

  if (doesRectangleContainPoint(leftHandleIconBoundingClientRect, point)) {
    return { handleType: HandleType.Left, directlyOnIcon: true };
  } else if (doesRectangleContainPoint(rightHandleIconBoundingClientRect, point)) {
    return { handleType: HandleType.Right, directlyOnIcon: true };
  }

  if (clientCoordinates.x >= leftHandleIconBoundingClientRect.right &&
    clientCoordinates.x <= rightHandleIconBoundingClientRect.left
  ) {
    return { handleType: HandleType.Both, directlyOnIcon: false };
  } else if (clientCoordinates.x <= leftHandleIconBoundingClientRect.right) {
    return { handleType: HandleType.Left, directlyOnIcon: false };
  }
  return { handleType: HandleType.Right, directlyOnIcon: false };
}

export function determineWhichHandleShouldBeDraggedForTouchAndOneHandle(
  clientCoordinates: { x: number, y: number },
  leftHandleInteractiveAreaBoundingClientRect: ClientRect
): DragHandle | undefined {

  const point = { left: clientCoordinates.x, top: clientCoordinates.y };
  // For touch we are "more loose" with our interpretation of what counts as being directly on the icon than mouse.
  const directlyOnIcon = doesRectangleContainPoint(leftHandleInteractiveAreaBoundingClientRect, point);
  if (directlyOnIcon) {
    return { handleType: HandleType.Left, directlyOnIcon: true };
  }
  // we don't allow the user to touch and drag anywhere on the track, like they can w/ a mouse, since that is
  // actually quite annoying on touch when the user is trying to scroll the page (instead of manipulate the slider).
  return undefined;
}

export function determineWhichHandleShouldBeDraggedForTouchAndTwoHandles(
  clientCoordinates: { x: number, y: number },
  leftHandleInteractiveAreaBoundingClientRect: ClientRect,
  rightHandleInteractiveAreaBoundingClientRect: ClientRect,
  rangeHandleInteractiveAreaBoundingClientRect: ClientRect
): DragHandle | undefined {

  const point = { left: clientCoordinates.x, top: clientCoordinates.y };
  // For touch we are "more loose" with our interpretation of what counts as being directly on the icon than mouse.
  const directlyOnLeftIcon = doesRectangleContainPoint(leftHandleInteractiveAreaBoundingClientRect, point);
  const directlyOnRightIcon = doesRectangleContainPoint(rightHandleInteractiveAreaBoundingClientRect, point);
  const directlyOnRangeHandle = doesRectangleContainPoint(rangeHandleInteractiveAreaBoundingClientRect, point);

  if (directlyOnLeftIcon && directlyOnRightIcon) {
    const distanceToLeftHandle = Math.abs(point.left - leftHandleInteractiveAreaBoundingClientRect.left);
    const distanceToRightHandle = Math.abs(point.left - rightHandleInteractiveAreaBoundingClientRect.right);
    const isCloserToLeftHandle = distanceToLeftHandle < distanceToRightHandle;

    return { handleType: isCloserToLeftHandle ? HandleType.Left : HandleType.Right, directlyOnIcon: true };
  } else if (directlyOnLeftIcon) {
    return { handleType: HandleType.Left, directlyOnIcon: true };
  } else if (directlyOnRightIcon) {
    return { handleType: HandleType.Right, directlyOnIcon: true };
  } else if (directlyOnRangeHandle) {
    return { handleType: HandleType.Both, directlyOnIcon: true };
  }
  // we don't allow the user to touch and drag anywhere on the track, like they can w/ a mouse, since that is
  // actually quite annoying on touch when the user is trying to scroll the page (instead of manipulate the slider).
  return undefined;
}
